/**
 * useEarlyAccessCounts — single source of truth for every PUBLIC Early Access
 * counter on the site.
 *
 * One shared pool: the first 100,000 Early Access members. The number shown is
 * always derived from REAL confirmed waitlist sign-ups (GET /api/early-access/
 * counts), so every surface that uses this hook stays in sync — header pill,
 * top banner, landing page, mobile stats, etc. As people join, `remaining`
 * ticks down and the per-type breakdown updates.
 *
 * Normalized output:
 *   {
 *     loading,
 *     claimed,    // confirmed sign-ups across all account types
 *     remaining,  // limit - claimed
 *     limit,      // 100,000
 *     full,       // claimed >= limit
 *     byType: { personal, business, government }  // confirmed per type
 *   }
 *
 * Anti-flash: stale-while-revalidate via localStorage so repeat navigations
 * never paint a blank/zero counter.
 */
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30_000;
const CACHE_KEY = 'jtr:earlyAccessCounts:v1';
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days hard cap

const DEFAULT_STATE = {
  loading: true,
  claimed: 0,
  remaining: 100000,
  limit: 100000,
  full: false,
  byType: { personal: 0, business: 0, government: 0 },
};

function normalize(data) {
  const total = (data && data.total) || {};
  const bt = (data && data.by_type) || {};
  const limit = Number(total.limit || 100000);
  const claimed = Number(total.confirmed || 0);
  const remaining = total.remaining != null ? Number(total.remaining) : Math.max(0, limit - claimed);
  return {
    loading: false,
    claimed,
    remaining,
    limit,
    full: Boolean(total.full),
    byType: {
      personal: Number((bt.personal && bt.personal.confirmed) || 0),
      business: Number((bt.business && bt.business.confirmed) || 0),
      government: Number((bt.government && bt.government.confirmed) || 0),
    },
  };
}

function readCache() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number' || !parsed.data) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* localStorage may be disabled — silent */
  }
}

export function useEarlyAccessCounts() {
  const [state, setState] = useState(() => {
    const cached = readCache();
    return cached ? { ...cached, loading: false } : DEFAULT_STATE;
  });

  useEffect(() => {
    let alive = true;
    const fetchCounts = async () => {
      try {
        const r = await fetch(`${API}/api/early-access/counts`, { credentials: 'omit' });
        if (!r.ok) return;
        const d = await r.json();
        const next = normalize(d);
        if (alive) {
          setState(next);
          writeCache(next);
        }
      } catch {
        /* public, non-critical — keep last known good */
      }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return state;
}

export default useEarlyAccessCounts;
